import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Activity, ArrowRight, Building2, Gavel, Radar, ShieldCheck, CircleDot, Grid2X2, Sparkles, Users } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ContactForm";
import { NetworkBackground } from "@/components/NetworkBackground";
import { NetworkSphere } from "@/components/NetworkSphere";
import { SEO } from "@/components/SEO";
import { essays } from "@/content/insights";

const scrollToContact = () => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });

const modelItems = [
  { icon: CircleDot, label: "dominant frames" },
  { icon: Grid2X2, label: "identity dimensions" },
  { icon: Sparkles, label: "emotional associations" },
  { icon: Users, label: "audience fragmentation" },
];

const situations = [
  {
    icon: Gavel,
    title: "Litigation & Media Exposure",
    text: "Before a filing or response becomes public. TruthForge shows how it will be framed and where it creates exposure. You only get one first version of the story.",
  },
  {
    icon: Building2,
    title: "Regulatory / Banking Pressure",
    text: "When a situation risks attracting scrutiny. TruthForge shows how perception can shift from issue to investigation.",
  },
  {
    icon: ShieldCheck,
    title: "High-Value Dispute / UHNW Exposure",
    text: "Where reputation, leverage, and positioning are intertwined. TruthForge shows where perception weakens your position and how it shifts.",
  },
];

const useCases = [
  ["01", "Before a legal or public move", "You are about to file, announce, or respond publicly. TruthForge tests how it will be interpreted before it becomes irreversible."],
  ["02", "During a live situation", "Narratives are forming in real time. TruthForge identifies how they are mutating and what your next move will trigger."],
  ["03", "Under adversarial pressure", "Opponents, media, or regulators will frame the situation. TruthForge surfaces their likely angles before they deploy them."],
  ["04", "For high-value disputes", "Reputation, leverage, and positioning are intertwined. TruthForge identifies where you are exposed and where you can shift the balance."],
];

const advantages = [
  { title: "Surface exposure before it becomes visible", text: "Map the invisible stress lines in public perception before they crack under pressure." },
  { title: "Avoid positions that collapse under scrutiny", text: "Pressure-test core arguments against aggressive counter-narratives before they harden." },
  { title: "Reduce reversals and reactive responses", text: "Stay ahead of the cycle so you are not forced into defensive pivots." },
  { title: "Align legal, communications, and leadership early", text: "Create a single source of truth for the strategic table before the crisis hits." },
  { title: "Act with a clear view of consequences", text: "Know how a statement, filing, or response may ripple through fragmented audience segments." },
];

const statusReadouts = ["DRIFT: ACTIVE", "SIGNAL: 87%", "FRAME LOCK: PENDING", "EXPOSURE MAP: LIVE"];

const telemetry = [
  ["01", "Dominant Frame", "Adversarial pressure"],
  ["02", "Audience Split", "Fragmented / volatile"],
  ["03", "Response Window", "Before hardening"],
];

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="TruthForge AI — See the Game. Win What Matters."
        description="A living model of how the world sees you. Test decisions before exposure locks your position. Narrative intelligence for high-stakes situations."
        path="/"
      />
      <Navigation />

      <main>
        <section className="relative flex min-h-[calc(100vh-108px)] items-center overflow-hidden bg-background px-5 py-24 md:px-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_42%,hsl(var(--primary)/0.16),transparent_38%)]" />
          <NetworkBackground />
          <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <div className="mb-6 inline-flex border border-primary/35 bg-surface/60 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-primary/90">
                Decision Support for High-Stakes Situations
              </div>
              <h1 className="max-w-4xl text-[clamp(42px,5.2vw,64px)] font-bold uppercase leading-[1.02] tracking-[-0.035em] text-foreground [word-spacing:-0.18em]">
                See the Game.<br />Shape the Frame.<br />Win What Matters.
              </h1>
              <p className="mt-6 max-w-[560px] text-lg font-normal leading-relaxed text-body md:text-xl">
                TruthForge builds a living model of how the world sees you, letting you test decisions against it before exposure locks your position. Because once the narrative hardens, your options collapse.
              </p>
              <div className="mt-6 flex flex-col gap-4 sm:flex-row">
                <button onClick={scrollToContact} className="flex h-11 w-full items-center justify-center rounded-[6px] bg-amber px-6 text-sm font-bold text-amber-foreground transition hover:bg-amber/90 sm:w-[260px]">
                  Request a Narrative Stress-Test
                </button>
                <button onClick={scrollToContact} className="flex h-11 w-full items-center justify-center rounded-[6px] border border-border bg-surface/40 px-6 text-sm font-bold text-foreground transition hover:border-primary/60 hover:bg-surface sm:w-[260px]">
                  Discuss a live situation
                </button>
              </div>
            </div>
            <div className="interface-panel scanline p-6 shadow-2xl">
              <div className="relative z-10 mb-5 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-body">
                <span>Narrative Twin</span>
                <span className="text-primary">Live Model</span>
              </div>
              <div className="relative z-10 grid min-h-[360px] place-items-center border border-border/35 bg-background/55">
                <Radar className="absolute h-56 w-56 text-primary/10" />
                <NetworkSphere />
              </div>
              <div className="relative z-10 mt-5 grid gap-3">
                {telemetry.map(([number, label, value]) => (
                  <div key={number} className="grid grid-cols-[44px_1fr] gap-4 border border-border/35 bg-background/40 px-4 py-3">
                    <span className="text-primary/80">{number}</span>
                    <p className="text-xs uppercase tracking-[0.12em] text-body"><span className="block text-foreground">{label}</span>{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 grid gap-3 border-t border-border/40 pt-5 text-[11px] uppercase tracking-[0.18em] text-body/75 sm:grid-cols-4">
              {statusReadouts.map((item) => <span key={item}>{item}</span>)}
            </div>
          </div>
        </section>

        <section id="living-model" className="relative scroll-mt-24 overflow-hidden bg-surface px-5 py-24 md:px-8">
          <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
            <div>
              <h2 className="mb-2 text-[32px] font-semibold leading-tight text-foreground">The Living Model</h2>
              <p className="max-w-xl text-xl leading-relaxed text-body">
                TruthForge creates a dynamic Digital Twin of how an entity exists in the collective mind. A living model composed of:
              </p>
              <div className="my-10 grid gap-4 sm:grid-cols-2">
                {modelItems.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-4 border border-border/40 bg-background/35 p-4 text-sm font-bold uppercase tracking-[0.08em] text-foreground">
                    <Icon className="h-5 w-5 text-primary" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <p className="max-w-xl text-xl leading-relaxed text-body">
                It detects structural drift and lets decisions be tested against real audience models before exposure. So you act with a full view of how the narrative will evolve.
              </p>
            </div>
            <div className="interface-panel scanline p-6 shadow-2xl">
              <div className="relative z-10 mb-5 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-body">
                <span>Narrative Twin</span>
                <span className="text-primary">Live Model</span>
              </div>
              <div className="relative z-10 grid min-h-[360px] place-items-center border border-border/35 bg-background/55">
                <Radar className="absolute h-56 w-56 text-primary/10" />
                <NetworkSphere />
              </div>
            </div>
          </div>
        </section>

        <section id="real-situations" className="scroll-mt-24 bg-background px-5 py-24 md:px-8">
          <div className="mx-auto max-w-7xl">
            <h2 className="mb-2 text-[32px] font-semibold leading-tight text-foreground">Real Situations</h2>
            <div className="grid gap-6 lg:grid-cols-3">
              {situations.map(({ icon: Icon, title, text }) => (
                <article key={title} className="interface-panel p-8 transition hover:border-primary/45 hover:brightness-110">
                  <Icon className="mb-8 h-7 w-7 text-primary" />
                  <h3 className="mb-6 text-2xl font-bold uppercase leading-snug">{title}</h3>
                  <p className="text-lg leading-relaxed text-body">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="use-cases" className="scroll-mt-24 bg-surface px-5 py-24 md:px-8">
          <div className="mx-auto max-w-7xl">
            <h2 className="mb-2 text-[32px] font-semibold leading-tight text-foreground">Where This Becomes Decisive</h2>
            <p className="text-xl italic text-body">What happens if we say this?</p>
            <div className="mt-20 grid gap-x-16 gap-y-20 lg:grid-cols-2">
              {useCases.map(([number, title, text]) => (
                <article key={number} className="grid grid-cols-[56px_1fr] gap-6">
                  <span className="text-3xl font-medium text-primary/70">{number}</span>
                  <div>
                    <h3 className="mb-6 text-2xl font-bold uppercase">{title}</h3>
                    <p className="max-w-lg text-lg leading-relaxed text-body">{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="advantage" className="scroll-mt-24 bg-background px-5 py-24 md:px-8">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.78fr_1.22fr]">
            <div>
              <h2 className="mb-6 text-[28px] font-semibold leading-tight text-foreground">The Advantage</h2>
              <blockquote className="max-w-md text-xl font-medium italic leading-relaxed text-foreground/80 md:text-[22px]">
                “This is not about being right. It is about not being caught wrong.”
              </blockquote>
              <p className="mt-7 max-w-md text-base leading-relaxed text-body">
                TruthForge gives teams a clearer view of exposure, consequence, and narrative risk before decisions become public.
              </p>
            </div>
            <div className="relative grid gap-10">
              <div className="absolute left-[5px] top-3 hidden h-[calc(100%-24px)] w-px bg-border/55 sm:block" />
              {advantages.map(({ title, text }) => (
                <article key={title} className="relative grid gap-4 sm:grid-cols-[36px_1fr] sm:gap-5">
                  <span className="mt-2 hidden h-[11px] w-[11px] rounded-full border border-primary/60 bg-primary/25 shadow-[0_0_18px_hsl(var(--primary)/0.38)] sm:block" />
                  <div>
                    <h3 className="text-[18px] font-semibold leading-snug text-foreground md:text-[20px]">{title}</h3>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-body/90">{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {essays[0] && (
          <section id="latest-thinking" className="scroll-mt-24 bg-background px-5 py-20 md:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="mb-10 flex items-end justify-between gap-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Latest Thinking</p>
                  <h2 className="mt-3 text-[28px] font-semibold leading-tight text-foreground md:text-[32px]">
                    Field analysis from the TruthForge team.
                  </h2>
                </div>
                <Link to="/insights" className="hidden text-xs font-bold uppercase tracking-[0.18em] text-body transition hover:text-foreground sm:inline-flex">
                  All Insights →
                </Link>
              </div>
              <Link
                to={`/insights/${essays[0].slug}`}
                className="interface-panel group block p-7 transition hover:border-primary/45 hover:brightness-110 md:p-10"
              >
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-bold uppercase tracking-[0.18em] text-body">
                  <span className="text-primary">{essays[0].tag}</span>
                  <span>{essays[0].readMinutes} min read</span>
                </div>
                <h3 className="mt-5 max-w-3xl text-2xl font-bold leading-snug text-foreground md:text-3xl">
                  {essays[0].title}
                </h3>
                <p className="mt-4 max-w-3xl text-base leading-relaxed text-body md:text-lg">{essays[0].dek}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary transition group-hover:gap-3">
                  Read essay <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </div>
          </section>
        )}

        <section id="contact-form" className="scroll-mt-24 bg-surface px-5 py-24 md:px-8">
          <div className="interface-panel scanline mx-auto max-w-[680px] p-7 shadow-2xl md:p-10">
            <div className="mb-9 text-center">
              <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary"><Activity className="h-4 w-4" /> Secure intake channel</div>
              <h2 className="mb-2 text-[32px] font-semibold leading-tight text-foreground">Know how it plays out before you act.</h2>
              <p className="text-body">TruthForge models how it will unfold before exposure. Tell us about your situation. All enquiries are treated as strictly confidential.</p>
            </div>
            <ContactForm source="homepage" variant="home" />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
